import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ContextUser } from '../providers/ContextUser';
import LoadingPage from '../components/loading-page';
import Layout from '../components/layout';
import PageUnauthorized from '../pages/unauthorized';

const PrivateRoute = ({ children }) => {
  const { isLoading, signedIn, currentUser, userHierarchy, getRedirectUrl } =
    useContext(ContextUser);
  const currentLocation = useLocation().pathname;

  if (isLoading) {
    return <LoadingPage label='Carregando' />;
  }

  if (!signedIn) {
    return <Navigate to='/acessar' replace />;
  }

  // Admin pode acessar qualquer rota
  if (currentUser?.role === 'ADMIN' || currentUser?.role === 'ACCESS_STAFF') {
    return <Layout>{children}</Layout>;
  }

  // Redireciono não-ADMINs que cheguem na raiz '/'
  if (currentLocation === '/') {
    const h = userHierarchy;
    let to = null;
    switch (currentUser?.role) {
      case 'ACCESS_ORG_LEVEL':
        to = h?.organizationId ? `/prefeitura/${h.organizationId}` : getRedirectUrl(currentUser.role, currentUser.allowedId);
        break;
      case 'ACCESS_BRANCH_LEVEL':
        to = h?.branchId ? `/prefeitura/secretaria/${h.branchId}` : getRedirectUrl(currentUser.role, currentUser.allowedId);
        break;
      case 'ACCESS_UNIT_LEVEL':
        to = h?.unitId ? `/prefeitura/secretaria/unidade/${h.unitId}` : getRedirectUrl(currentUser.role, currentUser.allowedId);
        break;
      case 'ACCESS_DEPARTMENT_LEVEL':
        to = h?.departmentId ? `/prefeitura/secretaria/unidade/departamento/${h.departmentId}` : getRedirectUrl(currentUser.role, currentUser.allowedId);
        break;
      default:
        to = getRedirectUrl(currentUser?.role, currentUser?.allowedId);
    }
    if (to && to !== '/') {
      return <Navigate to={to} replace />;
    }
  }

  // Rotas globais que usuários de nível de organização podem acessar
  const globalRoutes = [
    '/service-desk',
    '/models', 
    '/library',
    '/cybersecurity'
  ];

  // Prefixos por nível hierárquico
  const orgLevelPrefix = '/prefeitura/';
  const branchLevelPrefix = '/prefeitura/secretaria/';
  const unitLevelPrefix = '/prefeitura/secretaria/unidade/';
  const departmentLevelPrefix = '/prefeitura/secretaria/unidade/departamento/';

  // Fallback: enquanto a hierarquia não carrega, não bloquear pela ausência de IDs
  // A validação por prefixo (abaixo) cobre permissão por nível

  // Se for uma rota global, permite acesso para usuários de nível de organização e superiores
  if (['ACCESS_ORG_LEVEL', 'ACCESS_BRANCH_LEVEL', 'ACCESS_UNIT_LEVEL', 'ACCESS_DEPARTMENT_LEVEL'].includes(currentUser?.role) && 
      globalRoutes.some(route => currentLocation.startsWith(route))) {
    return <Layout>{children}</Layout>;
  }

  // Verifica acesso baseado no nível hierárquico por PREFIXO
  const isGlobalAllowed = globalRoutes.some(route => currentLocation.startsWith(route));
  if (isGlobalAllowed) {
    return <Layout>{children}</Layout>;
  }

  const path = currentLocation.endsWith('/') ? currentLocation : currentLocation + '/';
  switch (currentUser?.role) {
    case 'ACCESS_ORG_LEVEL':
      if (
        path.startsWith(orgLevelPrefix) ||
        path.startsWith(branchLevelPrefix) ||
        path.startsWith(unitLevelPrefix) ||
        path.startsWith(departmentLevelPrefix)
      ) {
        return <Layout>{children}</Layout>;
      }
      break;
    case 'ACCESS_BRANCH_LEVEL':
      if (
        path.startsWith(branchLevelPrefix) ||
        path.startsWith(unitLevelPrefix) ||
        path.startsWith(departmentLevelPrefix)
      ) {
        return <Layout>{children}</Layout>;
      }
      break;
    case 'ACCESS_UNIT_LEVEL':
      if (
        path.startsWith(unitLevelPrefix) ||
        path.startsWith(departmentLevelPrefix)
      ) {
        return <Layout>{children}</Layout>;
      }
      break;
    case 'ACCESS_DEPARTMENT_LEVEL':
      if (path.startsWith(departmentLevelPrefix)) {
        return <Layout>{children}</Layout>;
      }
      break;
    default:
      break;
  }

  let userLocationPermited = getRedirectUrl(
    currentUser?.role,
    currentUser?.allowedId,
  );

  // Fora das regras: sem permissão
  return (
    <Layout>
      <PageUnauthorized />
    </Layout>
  );
  
};
export default PrivateRoute;
